import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useAuth } from './useAuth';

/** Inaktivitäts-Timeout in Millisekunden (Standard: 30 Minuten) */
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

/** Events, die als Aktivität gewertet werden */
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

/**
 * Meldet den User automatisch ab, wenn er INACTIVITY_TIMEOUT_MS lang
 * keine Maus-/Tastatur-Interaktion hatte. Schützt offene Admin-Tabs
 * vor unbefugtem Zugriff.
 */
export function useInactivityLogout() {
  const { session } = useAuth();
  const nav = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(async () => {
      // Lock the admin UI locally without revoking the Supabase session,
      // so mobile app sessions remain active.
      try {
        localStorage.setItem('mk-admin-locked', 'true');
      } catch {
        // ignore localStorage write errors
      }
      notifications.show({
        color: 'yellow',
        title: 'Automatisch gesperrt',
        message: 'Admin-Oberfläche gesperrt wegen Inaktivität. Zum Fortfahren bitte erneut anmelden.',
        autoClose: 6000,
      });
      nav('/login', { replace: true });
    }, INACTIVITY_TIMEOUT_MS);
  }, [nav]);

  useEffect(() => {
    // Nur aktiv, wenn eine Session vorhanden ist
    if (!session) return;

    resetTimer();

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetTimer, { passive: true }));

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
  }, [session, resetTimer]);
}
